import React, { useState, useEffect } from 'react';
import { getPendingDeployments, getAllModulesForAdmin, getModuleLeaders, getSystemSettings, getModuleEnrollments } from '../../api/adminApi';

// Admin To-Do Dashboard: summarize tasks that need action
const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingDeploys, setPendingDeploys] = useState([]);
  const [modules, setModules] = useState([]);
  const [leaders, setLeaders] = useState([]);

  const fetchData = async () => {
    setLoading(true); setError(null);
    try {
      const [pd, mods, lds, sys] = await Promise.all([
        getPendingDeployments(),
        getAllModulesForAdmin(),
        getModuleLeaders(),
        getSystemSettings()
      ]);
      setPendingDeploys(pd || []);
      setModules(mods || []);
      setLeaders(lds || []);

      // Fetch enrollment counts for all modules in parallel
      const enrollPromises = (mods || []).map(m => getModuleEnrollments(m.module_id));
      const enrollResults = await Promise.all(enrollPromises);
      const enrollMap = {};
      (mods || []).forEach((m, i) => { enrollMap[m.module_id] = enrollResults[i]?.count || 0; });

      // Attach enrollment count and next deployment date to modules
      const parseMMDD = (val) => {
        if (!val) return null;
        const m = val.split('-');
        if (m.length !== 2) return null;
        const mm = parseInt(m[0],10); const dd = parseInt(m[1],10);
        if (isNaN(mm) || isNaN(dd)) return null;
        return { month: mm, day: dd };
      };

      const today = new Date();
      const modsEnriched = (mods || []).map(m => {
        const enrollCount = enrollMap[m.module_id] || 0;
        const settingKey = m.semester ? `semester_${m.semester}_deployment_date` : null;
        const settingVal = sys ? sys[settingKey] : null;
        const md = parseMMDD(settingVal);
        let nextDate = null;
        if (md) {
          let yr = today.getFullYear();
          const cand = new Date(yr, md.month - 1, md.day);
          if (cand < today) yr += 1;
          nextDate = new Date(yr, md.month - 1, md.day);
        }
        return { ...m, enrollCount, nextDeploymentDate: nextDate };
      });

      setModules(modsEnriched);
    } catch (err) {
      console.error('Admin To-Do load failed', err);
      setError(err.message || 'Failed to load admin dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) return <div className="text-center py-10 text-indigo-500 font-medium">Loading Admin To-Do...</div>;
  if (error) return <div className="p-6 bg-red-100 rounded-xl shadow-lg text-red-700 font-medium">Error: {error}</div>;

  // 1) Modules needing enrollment: any module with zero enrolled students (across years)
  const modulesMissingEnrollment = modules
    .filter(m => (m.enrollCount === 0 || m.enrollCount === undefined || m.enrollCount === null))
    .map(m => ({ module_id: m.module_id, module_name: m.module_name, nextDeploymentDate: m.nextDeploymentDate }))
    .sort((a,b) => {
      if (!a.nextDeploymentDate && !b.nextDeploymentDate) return 0;
      if (!a.nextDeploymentDate) return 1;
      if (!b.nextDeploymentDate) return -1;
      return new Date(a.nextDeploymentDate) - new Date(b.nextDeploymentDate);
    });

  // 2) Modules without assigned leader
  const modsWithoutLeader = modules.filter(m => !m.module_leader_id);

  // 3) Leaders with no modules assigned
  const leaderIdsWithModules = new Set(modules.filter(m => m.module_leader_id).map(m => m.module_leader_id));
  const leadersWithoutModules = leaders.filter(ld => !leaderIdsWithModules.has(ld.account_id));

  return (
    <div className="p-6 bg-white rounded-xl shadow-lg space-y-6">
      <h2 className="text-3xl font-bold text-indigo-700">Admin To-Do</h2>
      <p className="text-gray-600">Summary of actions requiring admin attention.</p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="p-4 border rounded">
          <h3 className="font-semibold text-indigo-700">Enrollments Missing</h3>
          {modulesMissingEnrollment.length === 0 ? (
            <div className="text-sm text-green-700 mt-2">All modules have enrollments.</div>
          ) : (
            <ul className="list-disc list-inside text-sm mt-2">
              {modulesMissingEnrollment.map(m => (
                <li key={m.module_id}>{m.module_name}{m.nextDeploymentDate ? ` — next: ${new Date(m.nextDeploymentDate).toLocaleDateString()}` : ''}</li>
              ))}
            </ul>
          )}
        </div>

        <div className="p-4 border rounded">
          <h3 className="font-semibold text-indigo-700">Modules Without Leaders</h3>
          {modsWithoutLeader.length === 0 ? (
            <div className="text-sm text-green-700 mt-2">All modules have assigned leaders.</div>
          ) : (
            <ul className="list-disc list-inside text-sm mt-2">
              {modsWithoutLeader.map(m => (
                <li key={m.module_id}>{m.module_name} (Module ID: {m.module_id})</li>
              ))}
            </ul>
          )}
        </div>

        <div className="p-4 border rounded">
          <h3 className="font-semibold text-indigo-700">Leaders Without Modules</h3>
          {leadersWithoutModules.length === 0 ? (
            <div className="text-sm text-green-700 mt-2">All leaders have module assignments.</div>
          ) : (
            <ul className="list-disc list-inside text-sm mt-2">
              {leadersWithoutModules.map(ld => (
                <li key={ld.account_id}>{ld.first_name} {ld.last_name} ({ld.email})</li>
              ))}
            </ul>
          )}
        </div>
      </div>

    </div>
  );
};

export default AdminDashboard;